import type { ApplicationRef, CargoApplicationRef } from '../../components/routes/RouteCreateForm/multiStopAllocationHelpers';
import type { DeliveryGroupDraft } from './deliveryGroupTypes';
import { computeMainUnloadingPosition, resolveGroupApplicationIds } from './deliveryGroupModel';
import type { ExecutionStopOption } from './executionDraftTypes';
import {
  buildExecutionTopologyPreview,
  formatExecutionUnloadingLabel,
  type ExecutionLabelContext,
  type ExecutionTopologyPreview
} from './executionTopologyHelpers';
import type { MainStopFormFields } from './routeStopsSavePipeline';

export type FlatStopForOptions = {
  position: number;
  type: string;
  pointType?: string | null;
  pointId?: number | null;
  address?: string | null;
  parentLoadingPosition?: number | null;
};

export function buildUnloadingStopOptionsForGroup(params: {
  group: DeliveryGroupDraft;
  topology?: ExecutionTopologyPreview | null;
  mainLoading?: MainStopFormFields;
  mainUnloading?: MainStopFormFields;
  labelContext?: ExecutionLabelContext;
  /** @deprecated legacy flatten — ignored when topology is provided */
  legacyStops?: FlatStopForOptions[];
  /** @deprecated use topology + labelContext */
  mainUnloadingLegacy?: FlatStopForOptions;
  resolveWarehouseLabel?: (id: number) => string;
  resolveApplicationLabel?: (id: number) => string;
  resolveConsigneeLabel?: (id: number) => string;
}): ExecutionStopOption[] {
  const labelContext: ExecutionLabelContext = params.labelContext ?? {
    resolveWarehouseName: (id) => {
      const raw = params.resolveWarehouseLabel?.(id);
      if (!raw) return undefined;
      return raw.replace(/^Склад\s*[—-]\s*/i, '').trim() || raw;
    },
    resolveConsigneeName: (id) => {
      const raw = params.resolveConsigneeLabel?.(id);
      if (!raw) return undefined;
      return raw.replace(/^Получатель\s*[—-]\s*/i, '').trim() || raw;
    }
  };

  let topology = params.topology ?? null;
  if (!topology && params.mainLoading && params.mainUnloading) {
    topology = buildExecutionTopologyPreview({
      groups: [params.group],
      mainLoading: params.mainLoading,
      mainUnloading: params.mainUnloading,
      labelContext
    });
  }

  if (topology) {
    const scoped = topology.unloadingOptionsByGroupUid.get(params.group.uid) ?? [];
    return scoped.map(({ value, label, stopUid }) => ({
      stopUid,
      label,
      groupUid: params.group.uid,
      position: value
    }));
  }

  return buildLegacyUnloadingStopOptions(params).map((o) => ({
    stopUid: `legacy-pos-${o.value}`,
    label: o.label,
    groupUid: params.group.uid,
    position: o.value
  }));
}

function buildLegacyUnloadingStopOptions(params: {
  group: DeliveryGroupDraft;
  legacyStops?: FlatStopForOptions[];
  mainUnloadingLegacy?: FlatStopForOptions;
  resolveWarehouseLabel?: (id: number) => string;
  resolveApplicationLabel?: (id: number) => string;
  resolveConsigneeLabel?: (id: number) => string;
}): Array<{ value: number; label: string }> {
  const { group, legacyStops = [], mainUnloadingLegacy } = params;
  const loadingPos = group.loadingPosition;
  const options: Array<{ value: number; label: string }> = [];

  for (const stop of group.unloadingStops) {
    if (stop.position == null) continue;
    options.push({
      value: stop.position,
      label: formatStopLabel(stop, {
        resolveConsigneeName: (id) => {
          const raw = params.resolveConsigneeLabel?.(id);
          return raw?.replace(/^Получатель\s*[—-]\s*/i, '').trim();
        },
        resolveWarehouseName: (id) => {
          const raw = params.resolveWarehouseLabel?.(id);
          return raw?.replace(/^Склад\s*[—-]\s*/i, '').trim();
        }
      })
    });
  }

  if (options.length === 0 && group.isDefault && mainUnloadingLegacy) {
    options.push({
      value: computeMainUnloadingPosition(
        legacyStops.map((s, i) => ({ ...s, position: i + 1 })) as any
      ),
      label: formatStopLabel(mainUnloadingLegacy, {
        resolveConsigneeName: (id) =>
          params.resolveConsigneeLabel?.(id)?.replace(/^Получатель\s*[—-]\s*/i, '').trim(),
        resolveWarehouseName: (id) =>
          params.resolveWarehouseLabel?.(id)?.replace(/^Склад\s*[—-]\s*/i, '').trim()
      })
    });
  }

  for (const s of legacyStops) {
    if (String(s.type).toLowerCase() !== 'unloading') continue;
    if (Number(s.parentLoadingPosition ?? 0) !== loadingPos) continue;
    const pos = s.position;
    if (options.some((o) => o.value === pos)) continue;
    options.push({
      value: pos,
      label: formatStopLabel(s, {
        resolveConsigneeName: (id) =>
          params.resolveConsigneeLabel?.(id)?.replace(/^Получатель\s*[—-]\s*/i, '').trim(),
        resolveWarehouseName: (id) =>
          params.resolveWarehouseLabel?.(id)?.replace(/^Склад\s*[—-]\s*/i, '').trim()
      })
    });
  }

  return options;
}

function formatStopLabel(
  stop: {
    pointType?: string | null;
    pointId?: number | null;
    address?: string | null;
    city?: string | null;
    contactPerson?: string | null;
  },
  labelContext?: ExecutionLabelContext
): string {
  return formatExecutionUnloadingLabel(
    {
      ...stop,
      pointType: stop.pointType as 'warehouse' | 'application' | 'consignee' | null | undefined
    },
    labelContext
  );
}

export function filterCargoForGroup(
  cargoRows: CargoApplicationRef[],
  group: DeliveryGroupDraft
): CargoApplicationRef[] {
  const allowed = resolveGroupApplicationIds(group);
  if (allowed.size === 0) return cargoRows;
  return cargoRows.filter((c) => allowed.has(Number(c.applicationId ?? c.application?.id)));
}

export function filterApplicationsForGroup(
  applications: ApplicationRef[],
  group: DeliveryGroupDraft
): ApplicationRef[] {
  const allowed = resolveGroupApplicationIds(group);
  if (allowed.size === 0) return applications;
  return applications.filter((a) => allowed.has(Number(a.id)));
}

export function isCargoInGroupScope(
  cargo: CargoApplicationRef,
  group: DeliveryGroupDraft,
  fromStopDraft: Record<number, number | undefined>,
  mainLoadingPos = 0
): boolean {
  const cargoId = Number(cargo.id);
  const fromPos = fromStopDraft[cargoId] ?? mainLoadingPos;
  return fromPos === group.loadingPosition;
}

export function groupStopPositions(group: DeliveryGroupDraft): number[] {
  const positions: number[] = [group.loadingPosition];
  if (group.isDefault) {
    positions.push(
      computeMainUnloadingPosition(
        group.unloadingStops.map((s, i) => ({ position: s.position ?? i + 1 })) as any
      )
    );
  }
  for (const s of group.unloadingStops) {
    if (s.position != null) positions.push(s.position);
  }
  for (const s of group.loadingStops) {
    if (s.position != null) positions.push(s.position);
  }
  return positions;
}
